import { Inject, inject, Injectable } from '@angular/core';
import { ISizeName } from '@df/core/service/client';
import { TimeoutService } from '@df/ng/timeout.service';
import { ICMS_CLIENT_CONFIG, NICmsClientConfig } from '@icms/client/config/icms-client.config';
import { BehaviorSubject, Subject } from 'rxjs';

export namespace NICmsAdminEditorArea {
    export const stateStorageKey = 'icmsAreaState';

    export interface IState extends NICmsClientConfig.ISize {
        fullSize: boolean;
    }
}

@Injectable()
export class ICmsAdminEditorAppAreaService {
    /**
     * Area state
     */
    readonly state: BehaviorSubject<NICmsAdminEditorArea.IState>;

    /**
     * State of zoom of the app wrapper
     */
    readonly scale = new BehaviorSubject(100);

    readonly busyRequest$ = new Subject<void>();
    readonly zoomOut$ = new BehaviorSubject<boolean>(false);

    private zoomOutTimeout?: number;

    private config = inject(ICMS_CLIENT_CONFIG);
    private timeoutService = inject(TimeoutService);

    constructor() {
        this.state = new BehaviorSubject(this.getStorageState() || this.getDefaultState());
        this.state.subscribe(state => this.setStorageState(state));
    }

    /**
     * Set state to given size
     * @param sizeId string size identifier
     */
    setSize(sizeId: ISizeName): void {
        const state = this.toState(sizeId);
        if (state) {
            this.state.next(state);
        }
    }

    /**
     * Sets full size mode (no height limits)
     * @param fullSize if true then height is not restricted
     */
    setFullSize(fullSize: boolean) {
        const state = this.state.value;
        if (state.fullSize !== fullSize) {
            this.state.next({ ...state, fullSize });
        }
    }

    /**
     * Gets current state basing on storage value
     */
    private getStorageState(): NICmsAdminEditorArea.IState | undefined {
        return this.toState(window.localStorage.getItem(NICmsAdminEditorArea.stateStorageKey));
    }

    /**
     * Saves current state in storage
     */
    private setStorageState(state = this.state.value): void {
        const stateId = this.toStateId(state);
        stateId
            ? window.localStorage.setItem(NICmsAdminEditorArea.stateStorageKey, stateId)
            : window.localStorage.removeItem(NICmsAdminEditorArea.stateStorageKey);
    }

    /**
     * Converts string size id to state object
     */
    private toState(stateId: string | null): NICmsAdminEditorArea.IState | undefined {
        if (stateId) {
            const [sizeId, fullSize] = stateId.split('.');
            const state = this.config.sizes.find(i => i.id === sizeId);
            if (state) {
                return {
                    ...state,
                    fullSize: !!Number(fullSize)
                };
            }
        }
    }

    /**
     * Converts state to string representation
     */
    private toStateId(state: NICmsAdminEditorArea.IState = this.state.value): string | null {
        if (!state) {
            return null;
        }
        return [state.id, state.fullSize ? 1 : 0].join('.');
    }

    /**
     * Retrieves default size state
     */
    private getDefaultState(): NICmsAdminEditorArea.IState {
        const defaultSize = this.config.sizes.find(i => i.default) || this.config.sizes[0];
        return { ...defaultSize, fullSize: false };
    }

    zoomOut(): Promise<void> {
        return new Promise<void>(resolve => {
            if (this.zoomOutTimeout) {
                clearTimeout(this.zoomOutTimeout);
            }
            if (this.zoomOut$.value) {
                resolve();
            } else {
                this.zoomOutTimeout = this.timeoutService.setTimeout(() => {
                    this.zoomOut$.next(true);
                    this.timeoutService.setTimeout(resolve, 500);
                }, 50);
            }
        });
    }

    zoomIn(): Promise<void> {
        return new Promise<void>(resolve => {
            if (this.zoomOutTimeout) {
                clearTimeout(this.zoomOutTimeout);
                this.zoomOutTimeout = undefined;
            }

            if (this.zoomOut$.value) {
                this.zoomOut$.next(false);
                this.timeoutService.setTimeout(resolve, 300);
            } else {
                resolve();
            }
        });
    }
}
